/**
 * Format star coins for display with proper localization
 * Handles large numbers with appropriate abbreviations for EN and PT-BR
 */

import { getBrazilianStatus } from '../../utils/detectBrazil';

/**
 * Format star coins amount based on locale
 * @param {number} amount - The star coins amount to format
 * @param {boolean} forceFullNumber - Force showing full number without abbreviation
 * @returns {string} Formatted star coins amount
 */
export const formatStarCoins = (amount, forceFullNumber = false) => {
  const isBrazilian = getBrazilianStatus();

  // Handle null/undefined
  if (amount == null) return '0';

  // Convert to number if string
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;

  // For small numbers or forced full display
  if (num < 1000 || forceFullNumber) {
    return num.toString();
  }

  // Brazilian Portuguese formatting (uses mil for thousands, not k)
  if (isBrazilian) {
    if (num >= 1000000) {
      // Millions: 1.5M -> 1,5mi
      const millions = (num / 1000000).toFixed(1);
      return millions.replace('.', ',') + 'mi';
    } else if (num >= 1000) {
      // Thousands: 1500 -> 1,5mil or 1500 -> 1.5mil
      const thousands = (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1);
      return thousands.replace('.', ',') + ' mil';
    }
  }

  // English formatting
  if (num >= 1000000) {
    // Millions: 1500000 -> 1.5M
    const millions = (num / 1000000).toFixed(1);
    return millions.replace(/\.0$/, '') + 'M';
  } else if (num >= 1000) {
    // Thousands: 1500 -> 1.5k
    const thousands = (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1);
    return thousands.replace(/\.0$/, '') + 'k';
  }

  return num.toString();
};

/**
 * Format star coins for compact mobile display
 * Shows abbreviated format for values over 999
 * @param {number} amount - The star coins amount to format
 * @returns {string} Formatted star coins amount
 */
export const formatStarCoinsCompact = (amount) => {
  const isBrazilian = getBrazilianStatus();

  // Handle null/undefined
  if (amount == null) return '0';

  // Convert to number if string
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;

  // For values under 1000, show exact amount
  if (num < 1000) {
    return num.toString();
  }

  // For values 1000-9999, show with k/mil
  if (num < 10000) {
    if (isBrazilian) {
      const thousands = (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1);
      return thousands.replace('.', ',') + 'mil';
    } else {
      const thousands = (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1);
      return thousands.replace(/\.0$/, '') + 'k';
    }
  }

  // For values 10k+, use more aggressive rounding
  if (num < 1000000) {
    if (isBrazilian) {
      const thousands = Math.floor(num / 1000);
      return thousands + 'mil';
    } else {
      const thousands = Math.floor(num / 1000);
      return thousands + 'k';
    }
  }

  // For millions
  if (isBrazilian) {
    const millions = (num / 1000000).toFixed(1);
    return millions.replace('.', ',') + 'mi';
  } else {
    const millions = (num / 1000000).toFixed(1);
    return millions.replace(/\.0$/, '') + 'M';
  }
};

export default formatStarCoins;